import { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { Loader2, MapPin, GraduationCap, Building2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Reusable Google Places Autocomplete component
 * 
 * @param {string} type - 'school' | 'city' - determines search types
 * @param {string} placeholder - Input placeholder text
 * @param {function} onSelect - Callback when place is selected: (place) => void
 * @param {string} value - Controlled input value (display name)
 * @param {function} onChange - Callback when input changes
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Disable input
 */
const GooglePlacesAutocomplete = ({
  type = 'city',
  placeholder = 'Search...',
  onSelect,
  value = '',
  onChange,
  className = '',
  disabled = false,
  countries = null, // e.g. ['ca']
  bounds = null,    // { sw: {lat,lng}, ne: {lat,lng} } for strict region biasing
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const sessionToken = useRef(null);
  const debounceTimer = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Initialize Google Places services
  useEffect(() => {
    const initGoogle = () => {
      if (window.google?.maps?.places) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        // Create a dummy element for PlacesService
        const dummyElement = document.createElement('div');
        placesService.current = new window.google.maps.places.PlacesService(dummyElement);
        // Create session token for billing optimization
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        setIsGoogleReady(true);
      }
    };

    // Check if Google is already loaded
    if (window.google?.maps?.places) {
      initGoogle();
    } else {
      // Wait for Google Maps to load
      const checkInterval = setInterval(() => {
        if (window.google?.maps?.places) {
          initGoogle();
          clearInterval(checkInterval);
        }
      }, 100);
      
      // Clear interval after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);
      
      return () => clearInterval(checkInterval);
    }
  }, []);

  // Update input value when external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Get search types based on component type
  const getSearchTypes = useCallback(() => {
    if (type === 'school') {
      // For schools: universities, colleges, schools
      return ['university', 'school'];
    } else {
      // For cities: localities, regions, countries
      return ['(cities)'];
    }
  }, [type]);

  // Fetch predictions from Google Places
  const fetchPredictions = useCallback((query) => {
    if (!autocompleteService.current || !query || query.length < 2) {
      setPredictions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const request = {
      input: query,
      sessionToken: sessionToken.current,
    };

    // Add type-specific options
    if (type === 'school') {
      request.types = ['university', 'school'];
    } else if (type === 'mixed') {
      // Mixed: schools, cities, neighborhoods. Google's Autocomplete only allows
      // one `types` value per request; we run two queries in parallel and merge.
      // Fall through — handled below.
    } else {
      request.types = ['(cities)'];
    }

    // Country restriction (ISO 3166-1 alpha-2, lowercased)
    if (countries && countries.length) {
      request.componentRestrictions = { country: countries };
    }

    // Optional bounds biasing. For 'mixed' (hero search) we bias toward Ontario
    // but DO NOT use strictBounds — users searching for non-Ontario places
    // (e.g. "Vancouver") still need to see suggestions so we can route them
    // into the expansion-request dialog.
    if (bounds && window.google?.maps?.LatLngBounds) {
      const latLngBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(bounds.sw.lat, bounds.sw.lng),
        new window.google.maps.LatLng(bounds.ne.lat, bounds.ne.lng)
      );
      request.bounds = latLngBounds;
      request.strictBounds = type !== 'mixed';
    }

    if (type === 'mixed') {
      // Run two requests (schools + cities/regions/neighborhoods) and merge.
      const requests = [
        { ...request, types: ['(regions)'] }, // cities, neighborhoods, postal codes
        { ...request, types: ['university', 'school'] },
      ];
      Promise.all(
        requests.map(
          (r) =>
            new Promise((resolve) => {
              autocompleteService.current.getPlacePredictions(r, (results, status) => {
                if (
                  status === window.google.maps.places.PlacesServiceStatus.OK &&
                  results
                ) {
                  resolve(results);
                } else {
                  resolve([]);
                }
              });
            })
        )
      ).then((groups) => {
        setIsLoading(false);
        // Interleave so users see a mix; de-dupe by place_id.
        const merged = [];
        const seen = new Set();
        const maxLen = Math.max(groups[0].length, groups[1].length);
        for (let i = 0; i < maxLen; i += 1) {
          for (let g = 0; g < groups.length; g += 1) {
            const item = groups[g][i];
            if (item && !seen.has(item.place_id)) {
              seen.add(item.place_id);
              merged.push(item);
            }
          }
        }
        setPredictions(merged.slice(0, 6));
      });
      return;
    }

    autocompleteService.current.getPlacePredictions(request, (results, status) => {
      setIsLoading(false);
      
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
        setPredictions(results.slice(0, 6)); // Limit to 6 results
      } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        setPredictions([]);
      } else {
        console.error('Places API error:', status);
        setPredictions([]);
      }
    });
  }, [type, countries, bounds]);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const query = e.target.value;
    setInputValue(query);
    setSelectedIndex(-1);
    
    if (onChange) {
      onChange(query);
    }

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Debounce the API call (300ms)
    debounceTimer.current = setTimeout(() => {
      fetchPredictions(query);
    }, 300);
  };

  // Handle place selection
  const handleSelectPlace = (prediction) => {
    if (!placesService.current) return;

    setIsLoading(true);
    
    placesService.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['name', 'geometry', 'formatted_address', 'address_components'],
        sessionToken: sessionToken.current,
      },
      (place, status) => {
        setIsLoading(false);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          // Create new session token for next search
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
          
          const result = {
            name: place.name,
            place_id: prediction.place_id,
            formatted_address: place.formatted_address,
            lat: place.geometry?.location?.lat(),
            lng: place.geometry?.location?.lng(),
          };

          // Extract address components for every type
          if (place.address_components) {
            place.address_components.forEach(component => {
              if (component.types.includes('locality')) {
                result.city = component.long_name;
              }
              if (component.types.includes('country')) {
                result.country = component.long_name;
                result.country_code = component.short_name;
              }
              if (component.types.includes('administrative_area_level_1')) {
                result.state = component.long_name;
                result.state_code = component.short_name;
              }
              if (component.types.includes('neighborhood') && !result.neighborhood) {
                result.neighborhood = component.long_name;
              }
              if (component.types.includes('sublocality') && !result.neighborhood) {
                result.neighborhood = component.long_name;
              }
            });
          }

          // Pass through prediction "kind" so callers can distinguish school vs city
          // even when address_components don't make it obvious.
          result.types = prediction.types || [];

          setInputValue(place.name);
          setPredictions([]);
          setIsFocused(false);
          
          if (onSelect) {
            onSelect(result);
          }
        }
      }
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!predictions.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < predictions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && predictions[selectedIndex]) {
          handleSelectPlace(predictions[selectedIndex]);
        }
        break;
      case 'Escape':
        setPredictions([]);
        setIsFocused(false);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setPredictions([]);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get icon based on type
  const getIcon = () => {
    if (type === 'school') {
      return <GraduationCap className="h-4 w-4 text-muted-foreground" />;
    }
    return <MapPin className="h-4 w-4 text-muted-foreground" />;
  };

  const getPredictionIcon = (prediction) => {
    if (type === 'school') {
      return <GraduationCap className="h-4 w-4 text-amber-500 flex-shrink-0" />;
    }
    return <MapPin className="h-4 w-4 text-amber-500 flex-shrink-0" />;
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
          {getIcon()}
        </div>
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || !isGoogleReady}
          className="pl-10 pr-10"
          autoComplete="off"
          data-testid={`places-autocomplete-${type}`}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      <AnimatePresence>
        {isFocused && (predictions.length > 0 || (inputValue.length >= 2 && !isLoading)) && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
            data-testid="places-dropdown"
          >
            {predictions.length > 0 ? (
              <ul className="py-1">
                {predictions.map((prediction, index) => (
                  <li key={prediction.place_id}>
                    <button
                      type="button"
                      onClick={() => handleSelectPlace(prediction)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${
                        index === selectedIndex 
                          ? 'bg-amber-50' 
                          : 'hover:bg-slate-50'
                      }`}
                      data-testid={`place-option-${index}`}
                    >
                      {getPredictionIcon(prediction)}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-900 truncate">
                          {prediction.structured_formatting?.main_text || prediction.description}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {prediction.structured_formatting?.secondary_text || ''}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : inputValue.length >= 2 && !isLoading ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                <Building2 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                No matching locations found
              </div>
            ) : null}
            
            {/* Google Attribution */}
            <div className="px-3 py-2 border-t bg-slate-50 flex items-center justify-end">
              <img 
                src="https://developers.google.com/static/maps/documentation/images/powered_by_google_on_white.png" 
                alt="Powered by Google" 
                className="h-4"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!isGoogleReady && (
        <p className="text-xs text-muted-foreground mt-1">
          Loading location search...
        </p>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
